import sum from 'lodash/sum';
import uniqBy from 'lodash/uniqBy';
import { createSlice } from '@reduxjs/toolkit';
// utils
import { selectFrom, selectOne, callRPC, insertInto, updateTable, deleteFrom } from '../../services/databaseService';
import { uploadFile, deleteFile } from '../../services/storageService';
import { invokeFunction } from '../../services/edgeFunctionService';
import { supabase } from '../../services/supabase';
import { VenueManager, VenueFiscal, VenueSchedule, VenueState, VenueSecurity, VenueSettings, VenueDocs, VenueMenu, VenueTableUpsert, TableQrPayload } from '../../@types/venue';
import { AssignableTable } from '../../@types/tab';
import { collection, query, where, getDocs } from 'firebase/firestore';
//
import { dispatch } from '../store';
import { UserManager } from '../../@types/user';
import { sortVenueSchedule } from '../../utils/sortVenueSchedule';
import { normalizeCnpjDigits } from '../../utils/cnpj';
import { toBrazilPhoneE164, normalizeAlnumId, normalizeZipDigits } from '../../utils/phone';

// ----------------------------------------------------------------------

// Helper function to get initial tenantVenue (organization_id from user or 'all' as fallback)
function getInitialTenantVenue(): string {
  // This will be set properly when user is loaded
  // For now, return 'all' as fallback (will be updated when user data is available)
  return 'all';
}

const initialState: VenueState = {
  isLoading: false,
  error: null,
  venues: [],
  tenantVenue: getInitialTenantVenue(), // Will be set to organization_id when user loads
  venue: null,
  fiscal: null,
  schedule: [],
  layout: [],
  security: [],
  settings: null,
  docs: null,
  menus: [],
  deleteSchedule: [],
  isOpenModal: false,
  sortBy: null,
  filters: {
    gender: [],
    category: 'All',
    colors: [],
    priceRange: [0, 200],
    rating: '',
  },
};

const slice = createSlice({
  name: 'venue',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // SET TENANT VENUE
    setTenantVenue(state, action) {
      state.isLoading = false
      state.tenantVenue = action.payload
    },

    // GET VENUES
    getVenuesSuccess(state, action) {
      state.isLoading = false;
      state.venues = action.payload;
    },

    // GET VENUE
    getVenueSuccess(state, action) {
      state.isLoading = false;
      state.venue = action.payload;
    },
    
    // GET VENUE AUTH
    getVenueAuthSuccess(state, action) {
      state.isLoading = false;
      state.security = action.payload;
    },

    // GET VENUE DOCS
    getVenueDocsSuccess(state, action) {
      state.isLoading = false;
      state.docs = action.payload;
    },

    // GET VENUE FISCAL
    getVenueFiscalSuccess(state, action) {
      state.isLoading = false;
      state.fiscal = action.payload;
    },

    // GET VENUE SCHEDULE
    getVenueScheduleSuccess(state, action) {
      state.isLoading = false;
      state.schedule = action.payload;
    },

    // GET VENUE SETTINGS
    getVenueSettingsSuccess(state, action) {
      state.isLoading = false;
      state.settings = action.payload;
    },

    // GET VENUE LAYOUT
    getVenueLayoutSuccess(state, action) {
      state.isLoading = false;
      state.layout = action.payload;
    },

    getVenueMenusSuccess(state, action) {
      state.isLoading = false;
      state.menus = action.payload;
    },

    deleteVenueSchedule(state, action) {
      const deleteSchedule = state.schedule.filter((item) => item.id !== action.payload.id);
      // state.deleteSchedule.push(action.payload)
      // const deletedArray = state.deleteSchedule.push(deleteSchedule)
      // state.deleteSchedule = deleteSchedule;

      state.isLoading = false;
      state.schedule = deleteSchedule;
    },

    //  SORT & FILTER VENUES
    sortByVenues(state, action) {
      state.sortBy = action.payload;
    },

    filterVenues(state, action) {
      state.filters.gender = action.payload.gender;
      state.filters.category = action.payload.category;
      state.filters.colors = action.payload.colors;
      state.filters.priceRange = action.payload.priceRange;
      state.filters.rating = action.payload.rating;
    },

     // OPEN MODAL
     openModal(state) {
      state.isOpenModal = true;
    },

    // CLOSE MODAL
    closeModal(state) {
      state.isOpenModal = false;
    },

  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  setTenantVenue,
  sortByVenues,
  filterVenues,
  openModal,
  closeModal,
} = slice.actions;

// ----------------------------------------------------------------------

export function getVenues() {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Use RPC function to get full venue data with schedule, settings, fiscal, and layout
      const venues = await callRPC('get_venues_full_data');

      dispatch(slice.actions.getVenuesSuccess(venues));
    } catch (error: any) {
      console.error('Error fetching venues:', error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getVenue(venueId: string) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const venue = await selectOne<any>('venues', { id: venueId });

      if (!venue) {
        throw new Error('Venue not found');
      }

      dispatch(slice.actions.getVenueSuccess(venue));
      return venue;
    } catch (error: any) {
      console.error('Error fetching venue:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function getVenueDocs(venueId: string) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const docs = await selectOne<any>('venue_docs', { venue_id: venueId });

      if (!docs) {
        // Return empty structure if no docs found
        dispatch(slice.actions.getVenueDocsSuccess({
          federalIdCard: null,
          companyContract: null,
          addressProof: null,
          bankingProof: null,
        }));
        return;
      }

      dispatch(slice.actions.getVenueDocsSuccess(docs));
      return docs;
    } catch (error: any) {
      console.error('Error fetching venue docs:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function getVenueFiscal(venueId: string) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const fiscal = await selectOne<any>('venue_fiscal', { venue_id: venueId });

      if (!fiscal) {
        dispatch(slice.actions.getVenueFiscalSuccess(null));
        return null;
      }

      dispatch(slice.actions.getVenueFiscalSuccess(fiscal));
      return fiscal;
    } catch (error: any) {
      console.error('Error fetching venue fiscal:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function getVenueSchedule(venueId: string) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const schedule = sortVenueSchedule(
        await selectFrom<any>('venue_schedule', {
          eq: { venue_id: venueId },
          order: { column: 'week_day', ascending: true },
        }),
      );

      dispatch(slice.actions.getVenueScheduleSuccess(schedule));
      return schedule;
    } catch (error: any) {
      console.error('Error fetching venue schedule:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function getVenueSecurity(venueId: string) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Use RPC function to get venue security with joins
      const { users } = await callRPC('get_venue_security', {
        p_venue_id: venueId,
      });

      console.log('Fetched venue security:',  users );

      dispatch(slice.actions.getVenueAuthSuccess( users ));
    } catch (error: any) {
      console.error('Error fetching venue security:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

// NOT BEING USED - PLACEHOLDER FOR WHEN THERE'RE MORE SEETINGS
export function getVenueSettings(venueId: string) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const settings = await selectOne<any>('venue_settings', { venue_id: venueId });

      if (!settings) {
        // Return default settings if not found
        dispatch(slice.actions.getVenueSettingsSuccess({}));
        return;
      }

      dispatch(slice.actions.getVenueSettingsSuccess(settings));
      return settings;
    } catch (error: any) {
      console.error('Error fetching venue settings:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function getVenueLayout(venueId: string) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const layout = await selectFrom<any>('venue_tables', {
        eq: { venue_id: venueId },
        order: { column: 'id', ascending: true },
      });

      dispatch(slice.actions.getVenueLayoutSuccess(layout));
      return layout;
    } catch (error: any) {
      console.error('Error fetching venue layout:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

// Persist the full table layout via secure RPC (upsert + soft-delete of removed).
export function updateVenueLayout(venueId: string, tables: VenueTableUpsert[]) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const result = await callRPC<{ venue_id: string; tables_count: number }[] | any>(
        'update_venue_layout',
        { p_venue_id: venueId, p_tables: tables }
      );
      // Refresh venues so the store layout reflects new ids / soft-deletes.
      // getVenues() sets isLoading=false on success.
      await dispatch(getVenues());
      return Array.isArray(result) ? result[0] : result;
    } catch (error: any) {
      console.error('Error updating venue layout:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

// Fetch (and lazily create) the public code + universal-link URL for a table.
export function getTableQrPayload(tableId: number) {
  return async () => {
    try {
      const payload = await callRPC<TableQrPayload>('get_table_qr_payload', {
        p_venue_table_id: tableId,
      });
      return payload;
    } catch (error: any) {
      console.error('Error fetching table QR payload:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

// Rotate (invalidate) a table's public code; returns the new payload.
export function rotateTablePublicCode(tableId: number) {
  return async () => {
    try {
      const payload = await callRPC<TableQrPayload>('rotate_table_public_code', {
        p_venue_table_id: tableId,
      });
      return payload;
    } catch (error: any) {
      console.error('Error rotating table public code:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

// List assignable tables for tab creation (excludes deleted/disabled).
export function listVenueTablesForAssign(venueId: string) {
  return async () => {
    try {
      const tables = await callRPC<AssignableTable[]>('list_venue_tables_for_assign', {
        p_venue_id: venueId,
      });
      return tables || [];
    } catch (error: any) {
      console.error('Error listing venue tables for assign:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

// Helper function to get venue_user_id from current auth user
async function getCurrentVenueUserId(): Promise<string | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data: venueUser } = await supabase
      .from('venue_users')
      .select('id')
      .eq('auth_user_id', user.id)
      .single();

    return venueUser?.id || null;
  } catch (error) {
    console.error('Error getting venue_user_id:', error);
    return null;
  }
}

// ----------------------------------------------------------------------

async function syncVenueProfileTypes(venueId: string, venueTypes: string[]) {
  const { error } = await supabase
    .from('venue_profile')
    .upsert(
      { venue_id: venueId, venue_types: venueTypes, updated_at: new Date().toISOString() },
      { onConflict: 'venue_id' },
    );

  if (error) {
    throw error;
  }
}

// ----------------------------------------------------------------------

export function createVenue(data: VenueManager) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Geocode only when coordinates were not filled by Places autocomplete
      let coordinates: { lat: number; lng: number };
      if (
        data.latitude != null &&
        data.longitude != null &&
        !Number.isNaN(data.latitude) &&
        !Number.isNaN(data.longitude)
      ) {
        coordinates = { lat: data.latitude, lng: data.longitude };
      } else {
        const fullAddress = `${data.street}, ${data.number}, ${data.city}, ${data.state}, ${data.country}`;
        const geocode = await invokeFunction<{
          geometry: {
            location: {
              lat: number;
              lng: number;
            };
          };
        }>('geocode', { address: fullAddress });

        coordinates = geocode.geometry.location;
      }

      // RETURNS TABLE → PostgREST returns an array of rows
      const created = await callRPC<
        { venue_id: string; name: string }[] | { venue_id: string; name: string }
      >('create_venue', {
        p_name: data.name,
        p_description: data.description || null,
        p_phonenumber: toBrazilPhoneE164(data.phonenumber || (data as any).phoneNumber) || data.phonenumber || (data as any).phoneNumber,
        p_street: data.street,
        p_number: data.number,
        p_complement: data.complement || null,
        p_city: data.city,
        p_state: data.state,
        p_country: data.country,
        p_zipcode: normalizeZipDigits(data.zipcode || (data as any).zipCode) || data.zipcode || (data as any).zipCode,
        p_latitude: coordinates.lat,
        p_longitude: coordinates.lng,
        p_payment_mode: data.settings.payment_mode,
        p_service_mode: data.settings.service_mode,
        p_entrance_fee: data.settings.entrance_fee || null,
        p_minamount: data.settings.min_amount || null,
        p_venue_types: data.venue_types || [],
      });
      const createdRow = Array.isArray(created) ? created[0] : created;
      const venueId = createdRow?.venue_id;
      if (!venueId) {
        throw new Error('create_venue did not return venue_id');
      }

      // Upload photo if provided
      if (data.avatarUrl) {
        // If avatarUrl is a File-like object, upload it and set the generated path
        if (typeof data.avatarUrl === 'object') {
          const ext = (data.avatarUrl as any).name?.split('.').pop() ?? 'jpg';
          const photoPath = `venues/${venueId}/photo_${Date.now()}.${ext}`;
          await uploadFile('venue-photos', photoPath, data.avatarUrl as File, {
            upsert: false,
          });

          // Update venue with photo URL
          await updateTable('venues', {
            photo_url: photoPath,
          }, { id: venueId });
        } else if (typeof data.avatarUrl === 'string') {
          // If avatarUrl is already a string (path), just update the venue record
          await updateTable('venues', {
            photo_url: data.avatarUrl,
          }, { id: venueId });
        }
      }

      // Fetch the created venue
      const venue = await selectOne<any>('venues', { id: venueId });
      dispatch(slice.actions.getVenueSuccess(venue));
      return venue;
    } catch (error: any) {
      console.error('Error creating venue:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function createVenueFiscal(data: VenueFiscal) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Handle both camelCase (from component) and snake_case (from type) field names
      const venueId = (data as any).venueId || data.venue_id;
      if (!venueId) {
        throw new Error('Venue ID is required');
      }

      // Handle geocoding if addressBol is false
      let latitude = '0';
      let longitude = '0';
      
      if (!data.addressbol) {
        const lat = (data as any).latitude;
        const lng = (data as any).longitude;
        if (lat != null && lng != null && !Number.isNaN(Number(lat)) && !Number.isNaN(Number(lng))) {
          latitude = String(lat);
          longitude = String(lng);
        } else {
          const fullAddress = `${data.street}, ${data.number}, ${data.city}, ${data.state}, ${data.country}`;
          try {
            const geocode = await invokeFunction<{
              geometry: {
                location: {
                  lat: number;
                  lng: number;
                };
              };
            }>('geocode', { address: fullAddress });

            const coordinates = geocode.geometry.location;
            latitude = coordinates.lat.toString();
            longitude = coordinates.lng.toString();
          } catch (geocodeError) {
            console.warn('Geocoding failed, continuing without coordinates:', geocodeError);
          }
        }
      } else {
        // Use venue's business address
        const venue = await selectOne<any>('venues', { id: venueId });
        if (venue) {
          latitude = venue.latitude?.toString() || '0';
          longitude = venue.longitude?.toString() || '0';
        }
      }

      // Handle both legalname and legalName (camelCase from component)
      const legalname = data.legalname || (data as any).legalName;
      if (!legalname) {
        throw new Error('Legal name is required');
      }

      // Use secure RPC function (uses auth.uid() internally)
      const fiscal = await callRPC<any>('create_venue_fiscal', {
        p_venue_id: venueId,
        p_legalname: legalname,
        p_addressbol: data.addressbol || false,
        p_phonenumber: toBrazilPhoneE164(data.phonenumber || (data as any).phoneNumber) || data.phonenumber || (data as any).phoneNumber || '',
        p_street: data.street,
        p_number: data.number,
        p_complement: data.complement || '',
        p_city: data.city,
        p_state: data.state,
        p_country: data.country,
        p_zipcode: normalizeZipDigits(data.zipcode || (data as any).zipCode) || data.zipcode || (data as any).zipCode || '',
        p_cnpj: normalizeCnpjDigits(data.cnpj) || data.cnpj,
        p_fiscalregime: data.fiscalregime,
        p_iestadual: normalizeAlnumId(data.iestadual) || null,
        p_imunicipal: normalizeAlnumId(data.imunicipal) || null,
        p_cdmunicipal: normalizeAlnumId(data.cdmunicipal) || null,
        p_latitude: latitude,
        p_longitude: longitude,
      });

      dispatch(slice.actions.getVenueFiscalSuccess(fiscal));
      return fiscal;
    } catch (error: any) {
      console.error('Error creating venue fiscal:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function createVenueSchedule(data: VenueManager) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Use RPC function to update venue schedule
      const result = await callRPC('update_venue_schedule', {
        p_venue_id: data.id,
        p_schedule: data.schedule,
      });

      // Fetch updated schedule
      const schedule = sortVenueSchedule(
        await selectFrom<any>('venue_schedule', {
          eq: { venue_id: data.id },
          order: { column: 'week_day', ascending: true },
        }),
      );

      dispatch(slice.actions.getVenueScheduleSuccess(schedule));
      return schedule;
    } catch (error: any) {
      console.error('Error creating venue schedule:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

// ----------------------------------------------------------------------

async function syncVenueEntranceFeeWeekday(
  venueId: string,
  overrides: { week_day: number; entrance_fee: number | null }[],
) {
  await Promise.all(
    overrides.map(async ({ week_day, entrance_fee }) => {
      if (entrance_fee === null) {
        await deleteFrom('venue_entrance_fee_weekday', { venue_id: venueId, week_day });
        return;
      }

      const { error } = await supabase
        .from('venue_entrance_fee_weekday')
        .upsert(
          {
            venue_id: venueId,
            week_day,
            entrance_fee,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'venue_id,week_day' },
        );

      if (error) {
        throw error;
      }
    }),
  );
}

// ----------------------------------------------------------------------

export function updateVenue(
  data: VenueManager,
  weekdayFeeOverrides?: { week_day: number; entrance_fee: number | null }[],
) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Update venue data
      const updated_data: any = {};
      
      if (data.name) updated_data.name = data.name;
      if (data.description !== undefined) updated_data.description = data.description;
      if (data.phonenumber) updated_data.phonenumber = data.phonenumber;
      if (data.street) updated_data.street = data.street;
      if (data.number) updated_data.number = data.number;
      if (data.complement !== undefined) updated_data.complement = data.complement;
      if (data.city) updated_data.city = data.city;
      if (data.state) updated_data.state = data.state;
      if (data.country) updated_data.country = data.country;
      if (data.zipcode) updated_data.zipcode = data.zipcode;
      if (data.latitude !== undefined) updated_data.latitude = data.latitude;
      if (data.longitude !== undefined) updated_data.longitude = data.longitude;
      if (data.status !== undefined) updated_data.status = data.status;
      if (data.openstatus !== undefined) updated_data.openstatus = data.openstatus;

      // Update venue record
      if (Object.keys(updated_data).length > 0) {
        await updateTable('venues', updated_data, { id: data.id });
      }

      // Update venue settings if provided
      if (data.settings.payment_mode || data.settings.service_mode || data.settings.entrance_fee !== undefined || data.settings.min_amount !== undefined) {
        const settingsUpdate: any = {};
        if (data.settings.payment_mode) settingsUpdate.payment_mode = data.settings.payment_mode;
        if (data.settings.service_mode) settingsUpdate.service_mode = data.settings.service_mode;
        if (data.settings.entrance_fee !== undefined) settingsUpdate.entrance_fee = data.settings.entrance_fee;
        if (data.settings.min_amount !== undefined) settingsUpdate.min_amount = data.settings.min_amount;

        await updateTable('venue_settings', settingsUpdate, { venue_id: data.id });
      }

      if (weekdayFeeOverrides && data.settings.payment_mode === 'postpaid') {
        await syncVenueEntranceFeeWeekday(data.id, weekdayFeeOverrides);
      }

      if (weekdayFeeOverrides && data.settings.payment_mode !== 'postpaid') {
        await Promise.all(
          [0, 1, 2, 3, 4, 5, 6].map((week_day) =>
            deleteFrom('venue_entrance_fee_weekday', { venue_id: data.id, week_day }),
          ),
        );
      }

      // Persist segment (venue_types) to venue_profile
      if (data.venue_types !== undefined) {
        await syncVenueProfileTypes(data.id, data.venue_types);
      }

      // Handle single photo upload
      if (data.avatarUrl && typeof data.avatarUrl === 'object') {
        const photoPath = `venues/${data.id}/photo_${Date.now()}.${data.avatarUrl.name.split('.').pop()}`;
        await uploadFile('venue-photos', photoPath, data.avatarUrl, {
          upsert: false,
        });

        await updateTable('venues', {
          photo_url: photoPath,
        }, { id: data.id });
      }

      // Handle multiple photo uploads
      if (data.avatarUrls && data.avatarUrls.length > 0) {
        const photoPaths: string[] = [];
        
        // Delete old photos if specified
        if (data.deletedPhoto_keys && data.deletedPhoto_keys.length > 0) {
          await Promise.all(
            data.deletedPhoto_keys.map((key) => deleteFile('venue-photos', key))
          );
        }

        // Upload new photos
        for (const avatarUrl of data.avatarUrls) {
          if (avatarUrl && typeof avatarUrl === 'object') {
            const photoPath = `venues/${data.id}/photo_${Date.now()}_${Math.random().toString(36).substring(7)}.${avatarUrl.name.split('.').pop()}`;
            await uploadFile('venue-photos', photoPath, avatarUrl, {
              upsert: false,
            });
            photoPaths.push(photoPath);
          } else if (typeof avatarUrl === 'string') {
            photoPaths.push(avatarUrl);
          }
        }

        // Update venue with photo URLs
        if (photoPaths.length > 0) {
          await updateTable('venues', {
            photo_urls: photoPaths,
          }, { id: data.id });
        }
      }

      // Fetch updated venue
      const venue = await selectOne<any>('venues', { id: data.id });
      dispatch(slice.actions.getVenueSuccess(venue));
      await dispatch(getVenues());
      return venue;
    } catch (error: any) {
      console.error('Error updating venue:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function updateVenueDocs(data: VenueDocs) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const updated_data: any = {};

      // Upload and update each document
      if (data.federalIdCard && typeof data.federalIdCard === 'object') {
        const docPath = `venues/${data.venueId}/docs/federalIdCard_${Date.now()}.${data.federalIdCard.name.split('.').pop()}`;
        await uploadFile('venue-docs', docPath, data.federalIdCard, {
          upsert: false,
        });
        updated_data.federal_id_card_url = docPath;
      }

      if (data.companyContract && typeof data.companyContract === 'object') {
        const docPath = `venues/${data.venueId}/docs/companyContract_${Date.now()}.${data.companyContract.name.split('.').pop()}`;
        await uploadFile('venue-docs', docPath, data.companyContract, {
          upsert: false,
        });
        updated_data.company_contract_url = docPath;
      }

      if (data.addressProof && typeof data.addressProof === 'object') {
        const docPath = `venues/${data.venueId}/docs/addressProof_${Date.now()}.${data.addressProof.name.split('.').pop()}`;
        await uploadFile('venue-docs', docPath, data.addressProof, {
          upsert: false,
        });
        updated_data.address_proof_url = docPath;
      }

      if (data.bankingProof && typeof data.bankingProof === 'object') {
        const docPath = `venues/${data.venueId}/docs/bankingProof_${Date.now()}.${data.bankingProof.name.split('.').pop()}`;
        await uploadFile('venue-docs', docPath, data.bankingProof, {
          upsert: false,
        });
        updated_data.banking_proof_url = docPath;
      }

      // Upsert venue_docs record
      if (Object.keys(updated_data).length > 0) {
        const existingDocs = await selectOne<any>('venue_docs', { venue_id: data.venueId });
        
        if (existingDocs) {
          await updateTable('venue_docs', updated_data, { venue_id: data.venueId });
        } else {
          await insertInto('venue_docs', {
            venue_id: data.venueId,
            ...updated_data,
          });
        }
      }

      // Fetch updated docs
      const docs = await selectOne<any>('venue_docs', { venue_id: data.venueId });
      dispatch(slice.actions.getVenueDocsSuccess(docs || {}));
      return docs;
    } catch (error: any) {
      console.error('Error updating venue docs:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function updateVenueFiscal(data: VenueFiscal) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Handle both camelCase (from component) and snake_case (from type) field names
      const venueId = (data as any).venueId || data.venue_id;
      if (!venueId) {
        throw new Error('Venue ID is required');
      }

      // Handle geocoding if addressBol is false and address changes
      let latitude: string | undefined;
      let longitude: string | undefined;
      
      if (!data.addressbol && (data.street || data.number || data.city || data.state)) {
        const lat = (data as any).latitude;
        const lng = (data as any).longitude;
        if (lat != null && lng != null && !Number.isNaN(Number(lat)) && !Number.isNaN(Number(lng))) {
          latitude = String(lat);
          longitude = String(lng);
        } else {
          const fullAddress = `${data.street || ''}, ${data.number || ''}, ${data.city || ''}, ${data.state || ''}, ${data.country || ''}`;
          try {
            const geocode = await invokeFunction<{
              geometry: {
                location: {
                  lat: number;
                  lng: number;
                };
              };
            }>('geocode', { address: fullAddress });

            const coordinates = geocode.geometry.location;
            latitude = coordinates.lat.toString();
            longitude = coordinates.lng.toString();
          } catch (geocodeError) {
            console.warn('Geocoding failed, continuing without coordinates:', geocodeError);
          }
        }
      } else if (data.addressbol) {
        // Use venue's business address
        const venue = await selectOne<any>('venues', { id: venueId });
        if (venue) {
          latitude = venue.latitude?.toString() || '0';
          longitude = venue.longitude?.toString() || '0';
        }
      }

      // Handle both legalname and legalName (camelCase from component)
      const legalname = data.legalname || (data as any).legalName;

      // Use secure RPC function (uses auth.uid() internally)
      const fiscal = await callRPC<any>('update_venue_fiscal', {
        p_venue_id: venueId,
        p_legalname: legalname || undefined,
        p_addressbol: data.addressbol !== undefined ? data.addressbol : undefined,
        p_phonenumber: toBrazilPhoneE164(data.phonenumber || (data as any).phoneNumber) || data.phonenumber || (data as any).phoneNumber || undefined,
        p_street: data.street || undefined,
        p_number: data.number || undefined,
        p_complement: data.complement !== undefined ? data.complement : undefined,
        p_city: data.city || undefined,
        p_state: data.state || undefined,
        p_country: data.country || undefined,
        p_zipcode: normalizeZipDigits(data.zipcode || (data as any).zipCode) || data.zipcode || (data as any).zipCode || undefined,
        p_cnpj: data.cnpj ? normalizeCnpjDigits(data.cnpj) || undefined : undefined,
        p_iestadual: data.iestadual !== undefined ? (normalizeAlnumId(data.iestadual) || undefined) : undefined,
        p_imunicipal: data.imunicipal !== undefined ? (normalizeAlnumId(data.imunicipal) || undefined) : undefined,
        p_cdmunicipal: data.cdmunicipal !== undefined ? (normalizeAlnumId(data.cdmunicipal) || undefined) : undefined,
        p_fiscalregime: data.fiscalregime || undefined,
        p_latitude: latitude,
        p_longitude: longitude,
      });

      dispatch(slice.actions.getVenueFiscalSuccess(fiscal));
      return fiscal;
    } catch (error: any) {
      console.error('Error updating venue fiscal:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function updateVenueSchedule(data: VenueManager) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Use secure RPC function (uses auth.uid() internally, no p_venue_user_id needed)
      const result = await callRPC('update_venue_schedule', {
        p_venue_id: data.id,
        p_schedule: data.schedule,
      });

      // Fetch updated schedule
      const schedule = sortVenueSchedule(
        await selectFrom<any>('venue_schedule', {
          eq: { venue_id: data.id },
          order: { column: 'week_day', ascending: true },
        }),
      );

      dispatch(slice.actions.getVenueScheduleSuccess(schedule));
      return schedule;
    } catch (error: any) {
      console.error('Error updating venue schedule:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function updateVenueSecurity(data: {
  venue_id: string;
  authorized_users: Array<{
    venue_user_id: string;
    role: string;
  }>;
  unauthorized_users: Array<{
    venue_user_id: string;
    role: string;
  }>;
}) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Update roles for authorized users
      const updatePromises = data.authorized_users.map((user) =>
        updateTable('venue_auth', { role: user.role }, {
          venue_id: data.venue_id,
          venue_user_id: user.venue_user_id,
        })
      );

      // Delete venue_auth for unauthorized users
      const deletePromises = data.unauthorized_users.map((user) =>
        deleteFrom('venue_auth', {
          venue_id: data.venue_id,
          venue_user_id: user.venue_user_id,
        })
      );

      await Promise.all([...updatePromises, ...deletePromises]);

      // Fetch updated auth users
      const authUsers = await selectFrom<any>('venue_auth', {
        select: `
          *,
          venue_users!venue_auth_venue_user_id_fkey (
            id,
            firstname,
            lastname,
            email,
            role,
            status
          )
        `,
        eq: { venue_id: data.venue_id },
      });

      dispatch(slice.actions.getVenueAuthSuccess(authUsers));
      return authUsers;
    } catch (error: any) {
      console.error('Error updating venue security:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function deleteVenueSchedule(item: {
  id: number;
  venue_id: string;
}) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      await deleteFrom('venue_schedule', {
        id: item.id,
        venue_id: item.venue_id,
      });

      dispatch(slice.actions.deleteVenueSchedule(item));
    } catch (error: any) {
      console.error('Error deleting venue schedule:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function getVenueMenus(venueId: string) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const result = await callRPC<{ menus: VenueMenu[] }>('get_venue_menus', {
        p_venue_id: venueId,
      });
      dispatch(slice.actions.getVenueMenusSuccess(result?.menus || []));
    } catch (error: any) {
      console.error('Error fetching venue menus:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function createVenueMenu(data: {
  venue_id: string;
  name: string;
  description?: string;
  is_default?: boolean;
  priority?: number;
  status?: number;
}) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const result = await callRPC<{ id: number }>('create_venue_menu', {
        p_venue_id: data.venue_id,
        p_name: data.name,
        p_description: data.description ?? null,
        p_is_default: data.is_default ?? false,
        p_priority: data.priority ?? 0,
        p_status: data.status ?? 1,
      });
      await dispatch(getVenueMenus(data.venue_id) as any);
      return result?.id;
    } catch (error: any) {
      console.error('Error creating venue menu:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function updateVenueMenu(data: {
  id: number;
  venue_id: string;
  name?: string;
  description?: string | null;
  is_default?: boolean;
  priority?: number;
  status?: number;
}) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      await callRPC('update_venue_menu', {
        p_menu_id: data.id,
        p_name: data.name ?? null,
        p_description: data.description ?? null,
        p_is_default: data.is_default ?? null,
        p_priority: data.priority ?? null,
        p_status: data.status ?? null,
      });
      await dispatch(getVenueMenus(data.venue_id));
    } catch (error: any) {
      console.error('Error updating venue menu:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function deleteVenueMenu(menuId: number, venueId: string) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      await callRPC('delete_venue_menu', { p_menu_id: menuId });
      await dispatch(getVenueMenus(venueId));
    } catch (error: any) {
      console.error('Error deleting venue menu:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function updateVenueMenuSchedules(menuId: number, venueId: string, scheduleIds: number[]) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      await callRPC('update_venue_menu_schedules', {
        p_menu_id: menuId,
        p_schedule_ids: scheduleIds,
      });
      await dispatch(getVenueMenus(venueId));
    } catch (error: any) {
      console.error('Error updating venue menu schedules:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function updateVenueMenuCategories(
  menuId: number,
  venueId: string,
  categories: { category_id: number; sort_order: number }[]
) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      await callRPC('update_venue_menu_categories', {
        p_menu_id: menuId,
        p_categories: categories,
      });
      await dispatch(getVenueMenus(venueId));
    } catch (error: any) {
      console.error('Error updating venue menu categories:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function bulkUpdateVenues(data: {
  venueIds: string[];
  updated_data: Partial<VenueManager> & { openStatus?: boolean };
}) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const openstatusValue =
        data.updated_data.openstatus ?? data.updated_data.openStatus;

      if (data.updated_data.status === undefined && openstatusValue === undefined) {
        return;
      }

      await callRPC('bulk_update_venues', {
        p_venue_ids: data.venueIds,
        p_status: data.updated_data.status ?? null,
        p_openstatus: openstatusValue ?? null,
      });

      dispatch(getVenues());
    } catch (error: any) {
      console.error('Error bulk updating venues:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}